"use client";

import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import { useCallback, useMemo, useState } from "react";
import type { SlideImage } from "yet-another-react-lightbox";
import { Heading } from "@repo/ui/heading";
import { CmsMedia, GalleryProps } from "./types";
import { absoluteCmsUrl, BaseImage } from "../base-image/BaseImage";

function toSlide(media: CmsMedia): SlideImage {
  const lg = media.formats?.large?.url;
  const md = media.formats?.medium?.url;
  const sm = media.formats?.small?.url;
  const src = absoluteCmsUrl(media.url || lg || md || sm);
  const srcSet = [
    media.formats?.small?.url && {
      src: absoluteCmsUrl(media.formats.small.url),
      width: media.formats.small.width,
    },
    media.formats?.medium?.url && {
      src: absoluteCmsUrl(media.formats.medium.url),
      width: media.formats.medium.width,
    },
    media.formats?.large?.url && {
      src: absoluteCmsUrl(media.formats.large.url),
      width: media.formats.large.width,
    },
    media.url && { src: absoluteCmsUrl(media.url), width: media.width },
  ].filter(Boolean) as { src: string; width: number }[];
  return {
    src,
    alt: media.alternativeText ?? media.caption ?? media.name,
    width: media.width,
    height: media.height,
    srcSet,
  } as SlideImage;
}

type GalleryItem = {
  id: number | string;
  title?: string | null;
  images?: CmsMedia[] | null;
  previewImage?: CmsMedia | null;
};

type FlexibleGalleryProps = {
  galleries?: GalleryItem[] | null; // legacy prop
  items?: GalleryItem[] | null; // Strapi sections.list-of-gallery
};

export default function GallerySlider({
  galleries,
  items,
}: FlexibleGalleryProps) {
  const [open, setOpen] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState<SlideImage[]>([]);

  const source: GalleryItem[] = useMemo(
    () =>
      Array.isArray(galleries) && galleries.length
        ? galleries
        : Array.isArray(items) && items.length
          ? items
          : [],
    [galleries, items],
  );

  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    mode: "snap",
    slides: { perView: 1.1, spacing: 10 },
    breakpoints: {
      "(min-width: 768px)": { slides: { perView: 2.2, spacing: 24 } },
      "(min-width: 1024px)": { slides: { perView: 1.7, spacing: 24 } },
    },
  });

  const prepared = useMemo(() => {
    return source.map((g) => {
      const slides = Array.isArray(g.images)
        ? g.images.map((img) => toSlide(img))
        : [];
      const preview = g.previewImage ? toSlide(g.previewImage) : slides[0];
      return {
        id: g.id,
        title: g.title ?? "Gallery",
        slides,
        preview,
      };
    });
  }, [source]);

  const handleOpen = useCallback(
    (idx: number) => {
      setSelectedGallery(prepared[idx]?.slides ?? []);
      setOpen(true);
    },
    [prepared],
  );

  if (!prepared.length) return null;
  return (
    <section className="md:py-14 py-10 reveal-stagger">
      <div className="max-w-7xl mx-auto w-full" id="gallery">
        <div className="max-w-2xl flex flex-col gap-6 md:py-12 py-12 pb-0 sm:mt-12">
          <Heading
            level="h2"
            className="xl:text-7xl md:text-6xl text-5xl max-w-2xl uppercase font-noto reveal"
            title="Our Gallery"
          />
          <div className="text-grey-900 md:text-lg text-base max-w-lg reveal">
            <p>
              Enjoy a visual journey through La Maison Suisse and discover the
              elegance, design, and atmosphere that define our spaces.
            </p>
          </div>
        </div>
      </div>
      <div
        ref={sliderRef}
        className="keen-slider py-8"
        aria-label="Galleries"
      >
        {prepared.map((g, index) => (
          <div
            key={g.id}
            className="keen-slider__slide min-w-[300px] relative"
            role="listitem"
          >
            <button
              type="button"
              className="relative md:h-[600px] h-[450px] w-full reveal-item overflow-hidden cursor-pointer text-left"
              onClick={() => handleOpen(index)}
              aria-label={`Open ${g.title}`}
            >
              {g.preview?.src ? (
                <BaseImage
                  src={g.preview.src}
                  alt={g.preview?.alt || g.title || "Gallery preview"}
                  fill
                  sizes="(max-width: 768px) 90vw, (max-width: 1024px) 60vw, 50vw"
                  priority={index === 0}
                  className="object-cover"
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center center'
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-500">No image available</span>
                </div>
              )}
              <p className="mt-2 text-white capitalize absolute bottom-4 left-4 text-lg">
                {g.title}
              </p>
            </button>
          </div>
        ))}
      </div>

      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={selectedGallery}
      />
    </section>
  );
}
